import { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { useDispatch } from 'react-redux'
import styled, { useTheme } from 'styled-components'
import { useCountWishlist } from '@/apis/rest/generated/hooks'
import { Spinner } from '@/components/common/etc'
import { CartEmpty } from '@/components/domains/cart/CartEmpty'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import { PRODUCT_CARD_SIZES } from '@/components/domains/products/new-product-card/new-product-card.type'
import { PageSizeEnum } from '@/constants/page-size.enum'
import { ProductLocation } from '@/constants/product-location.const'
import { useHomeTracker } from '@/containers/event-tracker/home.event-tracker'
import { useCustomRouter } from '@/containers/hooks'
import { useFrequentlyBoughtV2 } from '@/containers/hooks/use-frequently-bought-v2'
import { UserContainer } from '@/containers/users/UserContainer'
import { doSetHeaderTitle } from '@/stores/reduxUI'
import { ProductTypeDefinedByFrontend } from '@/types/product-type-defined-by.frontend'
import { transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'

interface FrequentlyBoughtProductListProps {
  isPage?: boolean
}

export const FrequentlyBoughtProductListV2 = ({ isPage }: FrequentlyBoughtProductListProps) => {
  const {
    fetchNextPage,
    data: frequentlyBoughtProducts,
    isFetching,
  } = useFrequentlyBoughtV2({
    page: PageSizeEnum.PageSize1,
    size: PageSizeEnum.PageSize20,
  })

  const { meIsLoading } = UserContainer.useContainer()
  const { query } = useCustomRouter()

  const { refetch: refetchCartCount } = useCountWishlist({
    query: {
      enabled: false,
    },
  })

  const { ref, inView } = useInView()

  const dispatch = useDispatch()
  const { color } = useTheme()

  const { trackClickHomeRecommendProductV2Event } = useHomeTracker()

  const handleProductClickV2 = (product: ProductTypeDefinedByFrontend) => {
    trackClickHomeRecommendProductV2Event({
      sectionName: '자주 산 상품',
      product,
      location: query.location as string,
      addLocation: 'more_list',
      listType: 'frequent_recommend',
    })
  }

  useEffect(() => {
    if (inView) {
      fetchNextPage()
    }
  }, [inView])

  if (frequentlyBoughtProducts[0]?.boughtCount === 1) {
    dispatch(doSetHeaderTitle('최근에 산 상품'))
  }
  if (frequentlyBoughtProducts[0]?.boughtCount !== undefined && frequentlyBoughtProducts[0]?.boughtCount >= 2) {
    dispatch(doSetHeaderTitle('자주 산 상품'))
  }

  if (!isPage && !meIsLoading && !isFetching && !frequentlyBoughtProducts.length) {
    return <CartEmpty isPersonalizationTab title="자주 산 상품" />
  }

  return (
    <div>
      {frequentlyBoughtProducts?.map((product, index) => {
        const productCard = transformProductTypeDefinedByFrontend(product)
        return (
          <StyledCardWrapper isLast={index === frequentlyBoughtProducts.length - 1} key={productCard.id} ref={ref}>
            <NewProductCard
              product={productCard}
              size={PRODUCT_CARD_SIZES.md}
              cartType="HORIZONTAL"
              label={{
                value: `${product.boughtCount}회 구매`,
                fontColor: color.grayWhite,
                variant: 'Body5 Bold',
                backgroundColor: color.blue500,
              }}
              onClickProductCard={() => handleProductClickV2(productCard)}
              trackerData={{
                sectionName: '자주 산 상품',
                listIndex: index,
                location: query.location as string,
                addLocation: query.add_location as ProductLocation,
              }}
              hasCartButton
              callbackLike={() => !isPage && refetchCartCount()}
            />
          </StyledCardWrapper>
        )
      })}
      {isFetching && <Spinner wrap />}
    </div>
  )
}

const StyledCardWrapper = styled.div<{ isLast: boolean }>`
  padding: ${({ theme }) => theme.spacing.size16};
  border-bottom: ${({
    isLast,
    theme: {
      color: { gray70 },
    },
  }) => (isLast ? 'none' : `0.1rem solid ${gray70}`)};
`
